import { ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, NgZone, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AuthenticationStore } from '../../states/auth/auth.store';
import { UserProfileStore } from '../../states/user-profile/user-profile.store';
import { NotificationHubService } from '../../services/notification/notification-hub.service';
import { SetupAttentionService } from '../../services/setup/setup-attention.service';
import { GuidedTourService } from '../../services/tour/guided-tour.service';
import { WikiAttentionService } from '../../services/wiki/wiki-attention.service';
import { WikiPanelService } from '../../services/wiki/wiki-panel.service';

@Component({
  selector: 'app-topbar',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent implements OnInit, OnDestroy {
  @Output() menuToggle = new EventEmitter<void>();

  private readonly authStore = inject(AuthenticationStore);
  readonly userProfileStore = inject(UserProfileStore);
  readonly notificationHubService = inject(NotificationHubService);
  readonly setupAttentionService = inject(SetupAttentionService);
  readonly guidedTourService = inject(GuidedTourService);
  readonly wikiAttentionService = inject(WikiAttentionService);
  readonly wikiPanelService = inject(WikiPanelService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  showNotifications = false;
  showTourButton = false;
  showGuidesButton = false;

  pageTitle   = 'Dashboard';
  currentTime = '';
  currentDate = '';

  private routerSub?: Subscription;
  private timerId?: ReturnType<typeof setInterval>;
  private notificationSub?: Subscription;
  private previousNotificationCount = 0;

  private routeLabels: Record<string, string> = {
    '/features/payroll':           'Payroll Dashboard',
    '/features/hr/dashboard':        'HR Dashboard',
    '/dashboard':      'Dashboard',
    '/employees':      'Employees',
    '/payroll/runs':   'Payroll Runs',
    '/payroll/config': 'Pay Configuration',
    '/payslips':       'Payslips',
    '/reports':        'Reports',
    '/settings':       'Settings',
    '/attendance':     'Attendance & Overtime',
    '/leaves':         'Leave Management',
    '/me/dashboard':   'My Dashboard',
    '/me/dtr':         'Daily Time Record',
    '/me/payslips':    'My Payslips',
    '/me/leaves':      'Leave Requests',
    '/me/overtime':              'Overtime Requests',
    '/me/time-corrections':       'Time Corrections',
    '/me/loans':                  'Loan Requests',
    '/me/attendance':             'My Attendance',
    '/me/biometrics':             'Biometric Logs',
    '/features/hr/leave-requests': 'Leave Management',
    '/features/hr/attendance-logs': 'Attendance Management',
    '/features/payroll/runs':     'Payroll Runs',
    '/hr/time-correction-requests': 'Time Correction Requests',
  };

  ngOnInit(): void {
    this.tick();
    this.zone.runOutsideAngular(() => {
      this.timerId = setInterval(() => this.zone.run(() => this.tick()), 30_000);
    });
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.setTitle(e.urlAfterRedirects));
    this.setTitle(this.router.url);

    this.notificationSub = this.notificationHubService.notifications$.subscribe(notifications => {
      const currentCount = notifications.length;

      if (this.previousNotificationCount > 0 && currentCount > this.previousNotificationCount) {
        this.showNotifications = true;
        this.wikiPanelService.close();
        this.cdr.markForCheck();
      }

      this.previousNotificationCount = currentCount;
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.timerId);
    this.routerSub?.unsubscribe();
    this.notificationSub?.unsubscribe();
  }

  toggleNotifications(event: Event): void {
    event.stopPropagation();
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) this.wikiPanelService.close();
    this.cdr.markForCheck();
  }

  toggleWiki(): void {
    this.wikiPanelService.toggle();
    if (this.wikiPanelService.isOpen()) this.showNotifications = false;
    this.cdr.markForCheck();
  }

  openSetupGuide(event: Event): void {
    event.stopPropagation();
    this.showNotifications = false;
    this.wikiPanelService.close();
    this.setupAttentionService.showModal();
    this.cdr.markForCheck();
  }

  openGuidesModal(event: Event): void {
    event.stopPropagation();
    this.wikiPanelService.close();
    this.showNotifications = false;
    this.wikiAttentionService.showModal();
    this.cdr.markForCheck();
  }

  startEmployeeTour(event: Event): void {
    event.stopPropagation();
    this.wikiPanelService.close();
    this.showNotifications = false;
    this.guidedTourService.replayEmployeePortalTour();
    this.cdr.markForCheck();
  }

  @HostListener('document:click')
  closeNotifications(): void {
    if (this.showNotifications || this.wikiPanelService.isOpen()) {
      this.showNotifications = false;
      this.wikiPanelService.close();
      this.cdr.markForCheck();
    }
  }

  onLogout(): void {
    this.authStore.logout();
  }

  private tick(): void {
    const now = new Date();
    this.currentTime = now.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });
    this.currentDate = now.toLocaleDateString('en-PH', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    this.cdr.markForCheck();
  }

  private setTitle(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    this.showTourButton = path === '/me' || path.startsWith('/me/');
    this.showGuidesButton = path.startsWith('/features') && this.wikiAttentionService.isEligibleUser();
    if (path.startsWith('/employees/') && path.endsWith('/edit')) { this.pageTitle = 'Edit Employee'; return; }
    if (path === '/employees/new') { this.pageTitle = 'Add Employee'; return; }
    if (path.startsWith('/payroll/runs/')) { this.pageTitle = 'Payroll Run Details'; return; }
    this.pageTitle = this.routeLabels[path] ?? 'Trieon HR';
  }
}

